<template>
	<!-- Top level -->
	<div :class="{ 'list': true, 'list--sectors': true, 'list--loading': loading }" v-if="depth === 1">
		<table :class="{ 'table' : true, 'table--loading' : loading }">
			<tr>
				<th>Sector</th>
				<th v-if="counts">Opportunities</th>
				<th v-if="counts">MADDs</th>
				<th v-if="counts">Intelligence</th>
				<th v-if="counts">Insights</th>
				<th v-if="counts">Experts</th>
				<th v-if="actionsSlotFilled"></th>
			</tr>

			<!-- Skeleton rows for laoding -->
			<template v-if="loading">
				<tr class="blank-row" v-for="i in 10">
					<td>&nbsp;</td>
					<td v-if="counts">&nbsp;</td>
					<td v-if="counts">&nbsp;</td>
					<td v-if="counts">&nbsp;</td>
					<td v-if="counts">&nbsp;</td>
					<td v-if="counts">&nbsp;</td>
					<td v-if="actionsSlotFilled">&nbsp;</td>
				</tr>
			</template>

			<template v-else v-for="sector in sectorList">
				<tr :class="sectorClasses(sector)">
					<td class="sector__name" @click.prevent="toggle(sector.id)">{{ sector.name }}</td>
					<td v-if="counts" class="opportunities">
						<router-link :to="'/opportunities?sectors[]=' + sector.id">{{ sector.opportunities }}</router-link>
					</td>
					<td v-if="counts" class="madds">
						<router-link :to="'/madds?sectors[]=' + sector.id">{{ sector.madds }}</router-link>
					</td>
					<td v-if="counts" class="intelligence">
						<router-link :to="'/intelligence?sectors[]=' + sector.id">{{ sector.intelligence }}</router-link>
					</td>
					<td v-if="counts" class="insights">
						<router-link :to="'/insights?sectors[]=' + sector.id">{{ sector.insights }}</router-link>
					</td>
					<td v-if="counts" class="sector-specialists">
						<router-link :to="{ name: 'sector-experts', params: { sector_id: sector.id } }">
							{{ sector.experts }}
						</router-link>
					</td>
					<td v-if="actionsSlotFilled" class="sector__actions">
						<slot name="actions" :sector="sector"></slot>
					</td>
				</tr>

				<tr class="children" v-show="children && sector.children.length && expanded === sector.id">
					<td :colspan="columnCount">
						<ListSectors v-if="actionsSlotFilled" :depth="depth + 1" :sectors="sector.children"
						             :counts="counts">
							<!-- Pass action slot to nested list -->
							<template slot="actions" slot-scope="{ sector }">
								<slot name="actions" :sector="sector"></slot>
							</template>
						</ListSectors>

						<ListSectors v-else :depth="depth + 1" :sectors="sector.children"
						             :counts="counts">
						</ListSectors>
					</td>
				</tr>
			</template>
		</table>

		<!-- Error while loading -->
		<div class="list__error" v-if="error">
			<span class="h2">Unable to load sectors.</span>
			<button class="btn btn--link" @click.prevent="load">Retry</button>
		</div>
	</div>

	<!-- Nested list, don't render container or column headings -->
	<table class="table" v-else>
		<template v-for="sector in sectorList">
			<tr :class="sectorClasses(sector)">
				<td class="sector__name" @click.prevent="toggle(sector.id)">{{ sector.name }}</td>
				<td v-if="counts">
					<router-link :to="'/opportunities?sectors[]=' + sector.id">{{ sector.opportunities }}</router-link>
				</td>
				<td v-if="counts">
					<router-link :to="'/madds?sectors[]=' + sector.id">{{ sector.madds }}</router-link>
				</td>
				<td v-if="counts">
					<router-link :to="'/intelligence?sectors[]=' + sector.id">{{ sector.intelligence }}</router-link>
				</td>
				<td v-if="counts">
					<router-link :to="'/insights?sectors[]=' + sector.id">{{ sector.insights }}</router-link>
				</td>
				<td v-if="counts">
					<router-link :to="{ name: 'sector-experts', params: { sector_id: sector.id } }">
						{{ sector.experts }}
					</router-link>
				</td>
				<td v-if="actionsSlotFilled" class="sector__actions">
					<slot name="actions" :sector="sector"></slot>
				</td>
			</tr>


			<tr class="children" v-show="sector.children.length && expanded === sector.id">
				<td :colspan="columnCount">
					<ListSectors v-if="actionsSlotFilled" :depth="depth + 1" :sectors="sector.children"
					             :counts="counts">
						<!-- Pass action slot to nested list -->
						<template slot="actions" slot-scope="{ sector }">
							<slot name="actions" :sector="sector"></slot>
						</template>
					</ListSectors>
					<ListSectors v-else :depth="depth + 1" :sectors="sector.children" :counts="counts"></ListSectors>
				</td>
			</tr>
		</template>
	</table>
</template>

<script>
	import API from './../../api';
	import Pagination from './Pagination';
	import FilterableList from './FilterableList';

	export default {
		name: 'ListSectors',
		mixins: [FilterableList],
		components: {
			Pagination,
		},
		props: {
			depth: {
				type: Number,
				default: 1,
			},
			sectors: {
				type: Array,
				default: null,
			},
			counts: {
				type: Boolean,
				default: true,
			},
			children: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				sectorList: this.sectors || [],
				expanded: null,
			}
		},
		computed: {
			actionsSlotFilled() {
				return !!this.$scopedSlots.actions;
			},
			columnCount() {
				let columns = 1;

				if(this.counts) {
					columns += 5;
				}

				if(this.actionsSlotFilled) {
					columns++;
				}

				return columns;
			},
		},
		methods: {
			load() {
				if(!this.sectors) {
					this.loading = true;

					API.getSectors({
                        hierarchical: true,
                        is_dropdown: false,
                    }).then(response => {
						this.loaded = true;
						this.sectorList = response;
					}).catch(() => {
						this.error = true;
					}).finally(() => {
						this.loading = false;
					});
				}
			},
			toggle(id) {
				if(!this.children) {
					return;
				}

				if(this.expanded === id) {
					this.expanded = null;
				} else {
					this.expanded = id;
				}
			},
			sectorClasses(sector) {
				let classes = ['sector', `sector--depth-${ this.depth }`];

				if(sector.children.length && this.children) {
					classes.push('sector--expandable');
				}

				if(this.expanded === sector.id) {
					classes.push('sector--expanded');
				}

				return classes.join(' ');
			},
		},
		created() {
			this.load();
		},
	}
</script>

<style lang="scss" scoped>
	@import '~reflex-grid/scss/includes/variables';
	@import '~reflex-grid/scss/includes/mixins';
	@import '~@/theme/_variables.scss';
	@import '~@/mixins/_class.scss';
	@import '~@/theme/_extenables.scss';

	td a {
		color: $link;
	}

	.children {
		> td {
			padding: 0;
		}

		td:not(:only-child) {
			border-bottom: 0.1rem dashed $secondary;
		}
	}

	.toolbar{
		display: none;
	}

	.sector {
		display: table-row;
		border: none;
		color: inherit;

		&.sector--expandable {
			.sector__name {
				cursor: pointer;

				@include respond-to(medium){
					display: block;
					width: 100%;
				}
			}
		}

		.sector__name {
			@include respond-to(medium){
				display: block;
				width: 100%;
			}
		}

		.sector__actions {
			text-align: right;
		}

		@for $i from 1 through 3 {
			&.sector--depth-#{$i} {
				> td:first-child {
					padding-left: $i * 2rem;

					@include respond-to(medium){
						padding-left: $i * 0.5rem;
					}
				}
			}
		}
	}

	.list--sectors{
		overflow-x:auto;
		.table{
			@include respond-to(medium){
				tr{
					display: block;
					border: none;
					padding: 0;

					th{
						padding: 1.7rem .2rem;
						display: none;
					}

					td{
						padding: 1.2rem .4rem 1rem;
						&:after{
							content: '';
							display: block;
							color: #657787;
							font-size: 1.1rem;
						}
						&.opportunities:after{
							content: 'Opportunities';
						}
						&.madds:after{
							content: 'MADDs';
						}
						&.intelligence:after{
							content: 'Intelligence';
						}
						&.insights:after{
							content: 'Insights';
						}
					}
				}
			}
		}
	}

</style>
